/**
 * Scene Composer – DreamMee MVP
 * Unified layer ordering scene with z-ordered background layers:
 *
 *   bgGroup:           'below' layers from selected background (fixed, non-reorderable)
 *   layerOrder groups:  unified, user-reorderable list of:
 *                         - element slots (character_1, car_1, etc.)
 *                         - 'above' overlay groups (cat, dog, box, etc.)
 *                         - 'text' overlay
 *   transformerLayer:   selection handles
 *
 * Design format: each design has a `backgrounds` array of selectable options.
 * Each option has a `layers` array: [{ src, zPosition: 'below'|'above', order }]
 *
 * All elements are draggable and resizable on the canvas.
 * Backgrounds come from the selected design template.
 * Car and Character are snapshots from their respective creators.
 *
 * Layer ordering is fully unified: characters, cars, overlays, and text
 * can be freely interleaved by the user (e.g. put a dog behind a character).
 */

class SceneComposer {
  constructor(canvasContainerId, controlsPanelId, characterCreator, carCreator, design, sceneConfig) {
    this.canvasContainerId = canvasContainerId;
    this.controlsPanel = document.getElementById(controlsPanelId);
    this.sceneConfig = sceneConfig;
    this.characterCreator = characterCreator;
    this.carCreator = carCreator;
    this.design = design; // selected DESIGN_TEMPLATE

    // Per-design canvas size override (falls back to global sceneConfig)
    const canvasOverride = design && design.canvasSize ? design.canvasSize : sceneConfig.canvas;
    this.canvasW = canvasOverride.width;
    this.canvasH = canvasOverride.height;

    // Dynamically adjust wrapper aspect ratio to match design dimensions and prevent clipping
    const wrapper = document.getElementById('scene-canvas-wrapper');
    if (wrapper) {
      wrapper.style.aspectRatio = `${this.canvasW} / ${this.canvasH}`;
    }

    this.creators = {};
    if (characterCreator) this.creators['character_1'] = characterCreator;
    if (carCreator) this.creators['car_1'] = carCreator;

    // Build layerGroups from design
    this.layerGroups = this.design ? (this.design.layerGroups || []) : [];

    // Get elements from design config
    this.elements = getDesignElements(this.design);

    // Sorted copy for rendering: elements with higher zOrder render on top.
    // If zOrder is not specified, array index is used (preserving original order).
    this._renderElements = [...this.elements]
      .map((el, i) => ({ ...el, _origIdx: i }))
      .sort((a, b) => (a.zOrder ?? a._origIdx) - (b.zOrder ?? b._origIdx));

    // Above overlay groups sorted by order (exclude custom templates — they use instances)
    this._aboveGroups = this.layerGroups
      .filter(g => g.zPosition === 'above' && !g.isCustomTemplate)
      .sort((a, b) => a.order - b.order);

    const hasFront = this.layerGroups.some(g => g.id === 'front');
    const hasBack = this.layerGroups.some(g => g.id === 'back');
    this.hasMultipleViews = hasFront && hasBack;

    this.state = {
      layerSelections: {},
      showText: this.design ? this.design.hasText !== false : true,
      // Per-element visibility keyed by slot/group
      elementVisibility: {},
      // Unified layer render order: elements + above overlay groups + text.
      // First = furthest back, last = front-most. User can reorder ALL items.
      layerOrder: this._buildDefaultLayerOrder(),
      activeView: 'back'
    };

    // Show reorder UI when there are 2+ items to reorder
    this._hasReorderable = this.state.layerOrder.length >= 2;

    // Initialize per-element visibility (all visible by default)
    this.elements.forEach(el => {
      this.state.elementVisibility[el.slot] = true;
    });

    // Initialize layer group visibility (skip custom templates — handled by _initCustomLayers)
    this.layerGroups.forEach(group => {
      if (!group.isCustomTemplate) {
        this.state.elementVisibility[group.id] = true;
      }
    });

    // Only build text state if design has text
    if (this.state.showText) {
      this.state.elementVisibility['text'] = true;
      this.state.text = { ...sceneConfig.textDefaults, ...(this.design && this.design.textDefaults ? this.design.textDefaults : {}) };
    }

    // Default layer selections (skip custom templates)
    this.layerGroups.forEach(group => {
      if (group.isCustomTemplate) return;
      if (this.design && this.design.defaultLayers && this.design.defaultLayers[group.id]) {
        this.state.layerSelections[group.id] = this.design.defaultLayers[group.id];
      } else if (group.variants && group.variants.length > 0) {
        this.state.layerSelections[group.id] = group.variants[0].id;
      }
    });

    // Private tracking objects
    this._layerOrderGroups = {}; // key -> Konva.Group
    this._elNodes = {};          // slot -> Konva.Image node (elements)
    this._elLoadIds = {};        // slot -> number (race-condition guard)
    this._elSavedPos = {};       // slot -> saved position object
    this._elPremadeSrc = {};     // slot -> premade image path
    this._overlayNodes = {};     // groupId -> Konva.Image node (overlay layers)
    this._overlaySavedPos = {};  // groupId -> saved position object
    this._customPremadeSelections = {}; // instanceKey -> { type, id, variantId?, src }

    // Initialize custom layers (creates instances, adds to layerOrder + state)
    if (this._initCustomLayers) {
      this._initCustomLayers();
      // Rebuild layerOrder now that custom instances exist
      this.state.layerOrder = this._buildDefaultLayerOrder();
      this._hasReorderable = this.state.layerOrder.length >= 2;
    }

    // Konva
    this.stage = null;
    this.mainLayer = null;
    this.bgGroup = null;       // z-layers with zPosition: 'below'
    this.transformerLayer = null;


    this.bgNode = null;
    this.textNode = null;
    this.transformer = null;

    this.imageCache = new Map();
    this.maxCacheSize = (typeof DREAMMEE_DEPLOY !== 'undefined' && DREAMMEE_DEPLOY.maxImageCacheSize) || 80;

    // Default positions: per-element from design config
    this._defaultPositions = {
      text: { x: 130, y: 120, scaleX: 1, scaleY: 1 }
    };
    // Build per-slot default positions from elements array
    this.elements.forEach(el => {
      const fallback = el.type === 'character'
        ? { centerX: 230, centerY: 146, scaleX: 1, scaleY: 1 }
        : { x: 40, y: 280, scaleX: 1, scaleY: 1 };
      const merged = { ...fallback, ...(el.defaultPosition || {}) };
      if (el.defaultPosition && (el.defaultPosition.x != null || el.defaultPosition.y != null)) {
        delete merged.centerX;
        delete merged.centerY;
      }
      this._defaultPositions[el.slot] = merged;
    });

    this.init();
  }

  setElementCreator(slot, creator) {
    this.creators[slot] = creator;
    if (this.elements) {
      const el = this.elements.find(e => e.slot === slot);
      if (el) {
        if (el.type === 'character') {
          this.characterCreator = creator;
        } else if (el.type === 'car') {
          this.carCreator = creator;
        }
      }
    }
    this.updateSlotThumbnail(slot);
  }

  updateSlotThumbnail(slot) {
    if (!this.controlsPanel) return;
    const card = this.controlsPanel.querySelector(`[data-layer-card="el_${slot}"]`);
    if (!card) return;
    const thumbContainer = card.querySelector('.layer-card__thumbnail');
    if (!thumbContainer) return;

    const el = this.elements.find(e => e.slot === slot);
    if (!el) return;

    const thumbnailSrc = this.getSlotThumbnailSrc(slot, el.type);
    const icon = el.type === 'character' ? '🧑' : '🚗';

    thumbContainer.innerHTML = thumbnailSrc
      ? `<img src="${thumbnailSrc}" alt="${el.label}" />`
      : `<span class="layer-card__fallback-icon">${icon}</span>`;
  }

  /**
   * Get layers from the currently selected layer variants, sorted by order.
   * Returns { below: [...], above: [...] } arrays.
   */
  getSelectedLayers() {
    if (!this.design) return { below: [], above: [] };
    const charCreator = this.creators['character_1'] || this.characterCreator;
    const activePose = charCreator ? charCreator.state.pose : (this.design.defaultElements?.character_1?.pose || 'male_tdnb');
    return resolveDesignLayers(this.design.id, this.state.layerSelections, activePose);
  }

  /**
   * Resolve default position for an element.  If the config specifies
   * centerX / centerY, compute the top-left x,y from the rendered
   * width/height (after scaling). Otherwise use the raw x,y.
   */
  resolveDefaultPos(type, renderedW, renderedH) {
    const def = this._defaultPositions[type];
    const scaleX = def.scaleX || 1;
    const scaleY = def.scaleY || 1;
    const rotation = def.rotation || 0;
    let x = def.x;
    let y = def.y;
    if (def.centerX != null) {
      x = def.centerX - (renderedW * scaleX) / 2;
    }
    if (def.centerY != null) {
      y = def.centerY - (renderedH * scaleY) / 2;
    }
    return { x, y, scaleX, scaleY, rotation };
  }

  async init() {
    this.initKonva();
    this.renderControls();
    await this.preloadBackgrounds();
    await this.renderScene();
  }

  deallocateCanvases(node) {
    if (!node) return;
    node.find('Image').forEach(imgShape => {
      const img = imgShape.image();
      if (img && img instanceof HTMLCanvasElement) {
        img.width = 0;
        img.height = 0;
      }
    });
  }

  destroy() {
    if (this.stage) {
      this.deallocateCanvases(this.stage);
      this.stage.destroy();
      this.stage = null;
    }
    this.creators = {};
    const container = document.getElementById(this.canvasContainerId);
    if (container) {
      container.innerHTML = '';
    }
  }

  /**
   * Build the default layerOrder: elements (by zOrder) interleaved with
   * above overlay groups (by order), then text at the end.
   * Elements are inserted first, then overlay groups are placed relative
   * to their design `order` values among elements.
   */
  _buildDefaultLayerOrder() {
    const items = [];

    // Add elements with their zOrder (default to index)
    this.elements.forEach((el, idx) => {
      items.push({
        id: el.slot,
        order: el.zOrder !== undefined ? el.zOrder : idx
      });
    });

    // Add above overlay groups with their order (non-custom-template only)
    this._aboveGroups.forEach((g, idx) => {
      items.push({
        id: g.id,
        order: g.order !== undefined ? g.order : 100 + idx
      });
    });

    // Add custom layer instances (from _initCustomLayers)
    if (this._customInstances) {
      this._customInstances.forEach((inst, idx) => {
        // Custom instances are ordered strictly by creation/stack index to stack correctly (e.g. Box 1 -> Box Car -> Box 2)
        items.push({
          id: inst.key,
          order: 5 + idx
        });
      });
    }

    // Sort by order ascending
    items.sort((a, b) => a.order - b.order);

    const order = items.map(item => item.id);

    // Text at the very end
    if (this.design && this.design.hasText !== false) {
      order.push('text');
    }

    return order;
  }

  initKonva() {
    const width = this.canvasW;
    const height = this.canvasH;

    this.stage = new Konva.Stage({
      container: this.canvasContainerId,
      width,
      height
    });

    // Layers (bottom → top):
    //   bgGroup          — background + 'below' layers (fixed)
    //   layerOrder groups — unified, user-reorderable: elements + overlays + text
    //   transformerLayer  — selection handles
    this.mainLayer = new Konva.Layer();
    this.bgGroup = new Konva.Group();
    this.transformerLayer = new Konva.Layer();

    this.mainLayer.add(this.bgGroup);

    // Create a Konva.Group for each entry in layerOrder (unified ordering)
    this._layerOrderGroups = {};
    this.state.layerOrder.forEach(key => {
      const isElement = this.elements.some(el => el.slot === key);
      const groupDef = this.layerGroups.find(g => g.id === key);
      const isDraggable = groupDef && groupDef.draggable;
      // Elements and text are always interactive; overlay groups only if draggable
      const listening = isElement || key === 'text' || isDraggable;
      const grp = new Konva.Group({ listening });
      this._layerOrderGroups[key] = grp;
      this.mainLayer.add(grp);

      // Initialize load counters for elements
      if (isElement) {
        this._elLoadIds[key] = 0;
      }
    });

    this.stage.add(this.mainLayer);
    this.stage.add(this.transformerLayer);

    // Transformer
    this.transformer = new Konva.Transformer({
      borderStroke: '#a855f7',
      borderStrokeWidth: 2,
      anchorStroke: '#a855f7',
      anchorFill: '#1e1e28',
      anchorSize: 20,
      anchorCornerRadius: 3,
      rotateEnabled: true,
      keepRatio: true,
      enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right']
    });
    this.transformerLayer.add(this.transformer);

    // Guidelines for snapping (Figma-like red/magenta lines)
    const centerX = width / 2;
    const centerY = height / 2;

    this.vGuideLine = new Konva.Line({
      points: [centerX, 0, centerX, height],
      stroke: '#ff007f',
      strokeWidth: 1.5,
      dash: [4, 4],
      visible: false,
      listening: false
    });

    this.hGuideLine = new Konva.Line({
      points: [0, centerY, width, centerY],
      stroke: '#ff007f',
      strokeWidth: 1.5,
      dash: [4, 4],
      visible: false,
      listening: false
    });

    this.transformerLayer.add(this.vGuideLine);
    this.transformerLayer.add(this.hGuideLine);

    // Deselect on background click
    this.stage.on('click tap', (e) => {
      // Do nothing if we clicked on the transformer or its handles
      if (e.target.getParent && e.target.getParent().className === 'Transformer') {
        return;
      }

      // If the clicked target is not one of our interactive nodes, deselect
      const allElementNodes = Object.values(this._elNodes);
      const allOverlayNodes = Object.values(this._overlayNodes);
      if (!allElementNodes.includes(e.target) && !allOverlayNodes.includes(e.target) && e.target !== this.textNode) {
        this.transformer.nodes([]);
        this.transformerLayer.batchDraw();
      }
    });

    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());
  }

  handleResize() {
    const container = document.getElementById(this.canvasContainerId);
    if (!container) return;
    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;
    const scale = containerWidth / this.canvasW;
    this.stage.width(this.canvasW * scale);
    this.stage.height(this.canvasH * scale);
    this.stage.scale({ x: scale, y: scale });
  }

  // ==================== PRELOAD ====================

  async preloadBackgrounds() {
    // Collect all layer src URLs from all variants
    const allUrls = [];
    for (const group of this.layerGroups) {
      for (const variant of group.variants) {
        if (variant.src) allUrls.push(variant.src);
      }
    }
    const unique = [...new Set(allUrls)];
    await Promise.allSettled(unique.map(src => this.loadImage(src)));
    console.log(`[Scene] Background layers preloaded: ${unique.length}`);
  }

  loadImage(src) {
    // Request downsized images from Shopify CDN (2× canvas width for Retina).
    // Admin pages get full-resolution (resolveAssetUrl skips maxWidth on admin).
    const maxWidth = this.canvasW * 2;
    const resolved = resolveAssetUrl(src, { maxWidth });
    return new Promise((resolve, reject) => {
      if (this.imageCache.has(resolved)) {
        resolve(this.imageCache.get(resolved));
        return;
      }
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        // LRU Cache eviction check
        if (this.imageCache.size >= this.maxCacheSize) {
          const oldestKey = this.imageCache.keys().next().value;
          this.imageCache.delete(oldestKey);
        }
        this.imageCache.set(resolved, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = resolved;
    });
  }

  loadImageFromDataUrl(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  // ==================== SCENE RENDERING ====================

  async renderScene() {
    await this.renderBackground();
    // Render all items in unified layerOrder
    for (const key of this.state.layerOrder) {
      const isElement = this.elements.some(el => el.slot === key);
      if (isElement) {
        await this.renderElement(key);
      } else if (key === 'text') {
        if (this.state.showText) await this.renderText();
      } else {
        // Overlay group — rendered via renderOverlay for the specific group
        await this.renderOverlayGroup(key);
      }
    }
  }

  /**
   * Move an overlay layer or text up (toward front) or down (toward back).
   * @param {string} key - Layer group ID or 'text'
   * @param {'up'|'down'} direction - 'up' = toward front (higher z), 'down' = toward back (lower z)
   */
  reorderLayer(key, direction) {
    const order = this.state.layerOrder;
    const idx = order.indexOf(key);
    if (idx < 0) return;

    const swapIdx = direction === 'up' ? idx + 1 : idx - 1;
    if (swapIdx < 0 || swapIdx >= order.length) return;

    [order[idx], order[swapIdx]] = [order[swapIdx], order[idx]];
    this.syncKonvaOrder();
    this.renderControls();
  }

  /**
   * Move an overlay layer to a specific index in layerOrder.
   * Used by drag-and-drop reordering.
   * @param {string} key - Layer group ID or 'text'
   * @param {number} newIdx - Target index in layerOrder
   */
  moveLayerToIndex(key, newIdx) {
    const order = this.state.layerOrder;
    const oldIdx = order.indexOf(key);
    if (oldIdx < 0 || oldIdx === newIdx) return;
    if (newIdx < 0 || newIdx >= order.length) return;

    order.splice(oldIdx, 1);
    order.splice(newIdx, 0, key);

    this.syncKonvaOrder();
    this.renderControls();
  }

  /**
   * Synchronize Konva group z-order to match this.state.layerOrder.
   * All interactive groups sit after bgGroup in the mainLayer.
   */
  syncKonvaOrder() {
    const baseZ = 1; // after bgGroup
    this.state.layerOrder.forEach((key, i) => {
      const grp = this._layerOrderGroups[key];
      if (grp) grp.setZIndex(baseZ + i);
    });
    this.mainLayer.batchDraw();
  }

  async renderBackground() {
    this.bgGroup.destroyChildren();

    // Base fill
    const bgRect = new Konva.Rect({
      x: 0, y: 0,
      width: this.canvasW,
      height: this.canvasH,
      fill: '#1a1a24'
    });
    this.bgGroup.add(bgRect);

    if (this.hasMultipleViews && this.state.activeView === 'front') {
      const { above } = this.getSelectedLayers();
      const frontLayer = above.find(l => l.groupId === 'front');
      if (frontLayer) {
        try {
          const img = await this.loadImage(frontLayer.src);
          const node = new Konva.Image({
            x: 0, y: 0,
            image: img,
            width: this.canvasW,
            height: this.canvasH
          });
          this.bgGroup.add(node);
        } catch (err) {
          console.warn(`[Scene] Front background layer failed:`, err);
        }
      }
    } else {
      const { below } = this.getSelectedLayers();
      for (const layer of below) {
        if (layer.groupId === 'front') continue;
        const group = this.layerGroups.find(g => g.variants.some(v => v.src === layer.src));
        if (group && this.state.elementVisibility[group.id] === false) {
          continue;
        }
        try {
          const img = await this.loadImage(layer.src);
          const node = new Konva.Image({
            x: 0, y: 0,
            image: img,
            width: this.canvasW,
            height: this.canvasH
          });
          this.bgGroup.add(node);
        } catch (err) {
          console.warn(`[Scene] Background layer failed:`, err);
        }
      }
    }

    this.mainLayer.batchDraw();
  }

  /**
   * Render 'above' overlay layers from the selected background.
   * Each above layer renders into its own Konva group (unified layer order).
   */
  async renderOverlay() {
    // Render all above overlay groups (non-custom-template)
    for (const group of this._aboveGroups) {
      await this.renderOverlayGroup(group.id);
    }
    // Render all custom layer instances
    if (this._customInstances) {
      for (const inst of this._customInstances) {
        await this.renderOverlayGroup(inst.key);
      }
    }
  }

  /**
   * Render a single overlay group by ID.
   * Clears existing content and re-renders the selected variant.
   * For groups with premadeOverlay config, renders a 3-layer composite:
   *   1. Box image (bottom)
   *   2. Premade character/car (middle, clipped to display area)
   *   3. Vertical line overlay (top)
   */
  async renderOverlayGroup(groupId) {
    const targetGroup = this._layerOrderGroups[groupId];
    if (!targetGroup) return;

    // Clear existing overlay node
    const existingNode = this._overlayNodes[groupId];
    if (existingNode && this.transformer && this.transformer.nodes().includes(existingNode)) {
      this.transformer.nodes([]);
      if (this.transformerLayer) this.transformerLayer.batchDraw();
    }
    targetGroup.destroyChildren();
    this._overlayNodes[groupId] = null;

    if (groupId === 'front') {
      this.mainLayer.batchDraw();
      return;
    }

    if (this.state.elementVisibility[groupId] === false || (this.hasMultipleViews && this.state.activeView === 'front')) {
      this.mainLayer.batchDraw();
      return;
    }

    const resolved = this._resolveGroupDef ? this._resolveGroupDef(groupId) : { groupDef: null };
    const groupDef = resolved.groupDef || this.layerGroups.find(g => g.id === groupId);
    if (!groupDef) return;

    const isDraggable = groupDef.draggable;
    const selectedId = this.state.layerSelections[groupId];
    const variantDef = groupDef.variants.find(v => v.id === selectedId);
    if (!variantDef || !variantDef.src) return;

    // Resolve src (may be pose-specific object)
    let src = variantDef.src;
    if (src && typeof src === 'object') {
      const charCreator = this.creators['character_1'] || this.characterCreator;
      const activePose = charCreator ? charCreator.state.pose : 'male_tdnb';
      src = src[activePose] || Object.values(src)[0];
    }
    if (!src) return;

    try {
      const img = await this.loadImage(src);

      if (isDraggable) {
        const defaultPos = variantDef.defaultPosition || { x: 0, y: 0, width: this.canvasW, height: this.canvasH };

        const pos = this._overlaySavedPos[groupId] || {
          x: defaultPos.x,
          y: defaultPos.y,
          scaleX: defaultPos.scaleX !== undefined ? defaultPos.scaleX : 1,
          scaleY: defaultPos.scaleY !== undefined ? defaultPos.scaleY : 1,
          rotation: defaultPos.rotation !== undefined ? defaultPos.rotation : 0,
          width: defaultPos.width,
          height: defaultPos.height
        };

        // Use a wrapper group for composite layers (box + premade + line)
        const wrapper = new Konva.Group({
          x: pos.x,
          y: pos.y,
          scaleX: pos.scaleX,
          scaleY: pos.scaleY,
          rotation: pos.rotation,
          draggable: true
        });

        // Layer 1: Box image (bottom)
        const boxNode = new Konva.Image({
          x: 0, y: 0,
          image: img,
          width: pos.width,
          height: pos.height
        });
        wrapper.add(boxNode);

        // Layer 2 & 3: Premade overlay + line (if premadeOverlay config exists and user selected one)
        const premadeOverlay = groupDef.premadeOverlay;
        const premadeSelection = this._customPremadeSelections[groupId];
        if (premadeOverlay && premadeSelection && premadeSelection.src) {
          await this._renderPremadeComposite(wrapper, premadeOverlay, premadeSelection, pos.width, pos.height, img.width, img.height);
        }

        wrapper.on('click tap', () => {
          this.transformer.nodes([wrapper]);
          this.transformerLayer.batchDraw();
        });

        const updatePos = () => {
          this._overlaySavedPos[groupId] = {
            x: wrapper.x(),
            y: wrapper.y(),
            scaleX: wrapper.scaleX(),
            scaleY: wrapper.scaleY(),
            rotation: wrapper.rotation(),
            width: pos.width,
            height: pos.height
          };
        };

        // Save initial position immediately (Group nodes don't have intrinsic width/height)
        updatePos();

        wrapper.on('dragend transformend', updatePos);

        // Pixel-perfect hit detection on the box image
        this._applyHitDetection(boxNode);

        this.enableSnapping(wrapper);
        targetGroup.add(wrapper);
        this._overlayNodes[groupId] = wrapper;
      } else {
        const node = new Konva.Image({
          x: 0, y: 0,
          image: img,
          width: this.canvasW,
          height: this.canvasH,
          listening: false
        });
        targetGroup.add(node);
      }
    } catch (err) {
      console.warn(`[Scene] Overlay layer failed (${groupId}):`, err);
    }

    this.mainLayer.batchDraw();
  }

  /**
   * Render premade character/car + line overlay inside a box wrapper group.
   * @param {Konva.Group} wrapper - The parent group for the box
   * @param {Object} premadeOverlay - Config: { catalog, lineSrc, premadeArea }
   * @param {Object} premadeSelection - User selection: { type, id, variantId?, src }
   * @param {number} renderW - Rendered box width
   * @param {number} renderH - Rendered box height
   * @param {number} nativeW - Native box image width (for scaling premadeArea)
   * @param {number} nativeH - Native box image height
   */
  async _renderPremadeComposite(wrapper, premadeOverlay, premadeSelection, renderW, renderH, nativeW, nativeH) {
    const area = premadeOverlay.premadeArea;
    if (!area) return;

    const clipX = area.x * renderW;
    const clipY = area.y * renderH;
    const clipW = area.width * renderW;
    const clipH = area.height * renderH;

    try {
      // Layer 2: Premade image, scaled to fit within the display area
      const premadeImg = await this.loadImage(premadeSelection.src);

      // Fit premade into clip area while preserving aspect ratio
      const premadeAspect = premadeImg.width / premadeImg.height;
      const areaAspect = clipW / clipH;
      let drawW, drawH, drawX, drawY;

      if (premadeAspect > areaAspect) {
        // Premade is wider — fit to width
        drawW = clipW;
        drawH = clipW / premadeAspect;
        drawX = clipX;
        drawY = clipY + (clipH - drawH); // Align to bottom of the window
      } else {
        // Premade is taller — fit to height
        drawH = clipH;
        drawW = clipH * premadeAspect;
        drawX = clipX + (clipW - drawW) / 2;
        drawY = clipY + (clipH - drawH); // Align to bottom of the window
      }

      const premadeNode = new Konva.Image({
        x: drawX, y: drawY,
        image: premadeImg,
        width: drawW,
        height: drawH
      });
      wrapper.add(premadeNode);
    } catch (err) {
      console.warn('[Scene] Box premade overlay failed:', err);
    }
  }

  /**
   * Set premade selection for a custom layer instance (box/box_car).
   * @param {string} instanceKey - e.g. 'box:0'
   * @param {Object} premadeData - { type: 'character'|'car', id, variantId?, src }
   */
  setCustomPremade(instanceKey, premadeData) {
    this._customPremadeSelections[instanceKey] = premadeData;
    this.renderOverlayGroup(instanceKey);
  }

  /**
   * Clear premade selection for a custom layer instance.
   * @param {string} instanceKey - e.g. 'box:0'
   */
  clearCustomPremade(instanceKey) {
    delete this._customPremadeSelections[instanceKey];
    this.renderOverlayGroup(instanceKey);
  }

  /**
   * Render a single element by slot ID. Works for both car and character types.
   * Handles premade sources, builder snapshots, and visibility toggling.
   */
  async renderElement(slot) {
    const elConfig = this.elements.find(e => e.slot === slot);
    if (!elConfig) return;

    const group = this._layerOrderGroups[slot];
    if (!group) return;

    const loadId = ++this._elLoadIds[slot];
    const existingNode = this._elNodes[slot];
    if (existingNode && this.transformer && this.transformer.nodes().includes(existingNode)) {
      this.transformer.nodes([]);
      if (this.transformerLayer) this.transformerLayer.batchDraw();
    }
    group.destroyChildren();
    this._elNodes[slot] = null;

    // Check visibility
    if (!this.state.elementVisibility[slot] || (this.hasMultipleViews && this.state.activeView === 'front')) {
      this.mainLayer.batchDraw();
      return;
    }

    const isChar = elConfig.type === 'character';
    const isCar = elConfig.type === 'car';
    const premadeSrc = this._elPremadeSrc[slot];
    const creator = this.creators[slot] || (isChar ? this.characterCreator : (isCar ? this.carCreator : null));
    const sizeFn = isChar ? sceneCharacterSize : sceneCarSize;

    // Premade image path: load PNG directly
    if (premadeSrc) {
      try {
        const img = await this.loadImage(premadeSrc);
        if (this._elLoadIds[slot] !== loadId) return;

        const dims = sizeFn(img.width, img.height);
        const pos = this._elSavedPos[slot] || this.resolveDefaultPos(slot, dims.width, dims.height);

        const node = new Konva.Image({
          x: pos.x, y: pos.y,
          image: img,
          width: dims.width,
          height: dims.height,
          scaleX: pos.scaleX,
          scaleY: pos.scaleY,
          rotation: pos.rotation,
          draggable: true
        });

        node.on('click tap', () => {
          this.transformer.nodes([node]);
          this.transformerLayer.batchDraw();
        });

        const updatePos = () => {
          this._elSavedPos[slot] = {
            x: node.x(),
            y: node.y(),
            scaleX: parseFloat(node.scaleX().toFixed(3)),
            scaleY: parseFloat(node.scaleY().toFixed(3)),
            rotation: parseFloat(node.rotation().toFixed(1))
          };
        };
        node.on('dragend transformend', updatePos);

        // Pixel-perfect hit detection (ignore transparent areas)
        this._applyHitDetection(node);

        this.enableSnapping(node);
        group.add(node);
        this._elNodes[slot] = node;
      } catch (err) {
        console.warn(`[Scene] Premade ${elConfig.type} load failed (${slot}):`, err);
      }
      this.mainLayer.batchDraw();
      return;
    }

    // Builder snapshot path
    if (!creator) {
      this.mainLayer.batchDraw();
      return;
    }

    const dataUrl = creator.toDataURL();
    try {
      const img = await this.loadImageFromDataUrl(dataUrl);
      if (this._elLoadIds[slot] !== loadId) return;

      const dims = sizeFn(img.width, img.height);
      const pos = this._elSavedPos[slot] || this.resolveDefaultPos(slot, dims.width, dims.height);

      const node = new Konva.Image({
        x: pos.x, y: pos.y,
        image: img,
        width: dims.width, height: dims.height,
        scaleX: pos.scaleX,
        scaleY: pos.scaleY,
        rotation: pos.rotation,
        draggable: true
      });

      node.on('click tap', () => {
        this.transformer.nodes([node]);
        this.transformerLayer.batchDraw();
      });

      const updatePos = () => {
        this._elSavedPos[slot] = {
          x: node.x(),
          y: node.y(),
          scaleX: parseFloat(node.scaleX().toFixed(3)),
          scaleY: parseFloat(node.scaleY().toFixed(3)),
          rotation: parseFloat(node.rotation().toFixed(1))
        };
      };
      node.on('dragend transformend', updatePos);

      // Pixel-perfect hit detection (ignore transparent areas)
      this._applyHitDetection(node);

      this.enableSnapping(node);
      group.add(node);
      this._elNodes[slot] = node;
    } catch (err) {
      console.warn(`[Scene] ${elConfig.type} snapshot failed (${slot}):`, err);
    }

    this.mainLayer.batchDraw();
  }

  async renderText() {
    // Text renders into unified _layerOrderGroups['text']
    const textContainer = this._layerOrderGroups?.['text'];

    if (this.textNode && this.transformer && this.transformer.nodes().includes(this.textNode)) {
      this.transformer.nodes([]);
      if (this.transformerLayer) this.transformerLayer.batchDraw();
    }
    textContainer.destroyChildren();
    this.textNode = null;

    if (this.state.elementVisibility['text'] === false || (this.hasMultipleViews && this.state.activeView === 'front')) {
      this.mainLayer.batchDraw();
      return;
    }

    const { text, fontFamily, fontSize, fill, x, y, scaleX = 1, scaleY = 1, rotation = 0, shadow = false, align, width } = this.state.text;

    // Force wait for custom font loading if fonts API is available
    if (document.fonts) {
      try {
        await document.fonts.load(`bold ${fontSize}px "${fontFamily}"`);
      } catch (err) {
        console.warn(`[Scene] Failed to load font "${fontFamily}":`, err);
      }
    }

    const textOptions = {
      x, y,
      text, fontFamily, fontSize, fill,
      scaleX, scaleY,
      rotation,
      fontStyle: 'bold',
      draggable: true,
      padding: 4
    };

    if (align) textOptions.align = align;
    if (width) textOptions.width = width;

    if (shadow) {
      textOptions.shadowColor = '#000000';
      textOptions.shadowBlur = 0;
      textOptions.shadowOffset = { x: 1.5, y: 1.5 };
      textOptions.shadowOpacity = 1;
      textOptions.stroke = '#000000';
      textOptions.strokeWidth = 1.5;
      textOptions.fillAfterStrokeEnabled = true;
    }

    this.textNode = new Konva.Text(textOptions);

    this.textNode.on('click tap', () => {
      this.transformer.nodes([this.textNode]);
      this.transformerLayer.batchDraw();
    });

    this.textNode.on('dblclick dbltap', () => {
      const newText = prompt('Enter text:', this.state.text.text);
      if (newText !== null) {
        this.state.text.text = newText;
        this.textNode.text(newText);
        textContainer.batchDraw();
        const input = document.getElementById('scene-text-input');
        if (input) input.value = newText;
      }
    });

    const updateTextPos = () => {
      this.state.text.x = this.textNode.x();
      this.state.text.y = this.textNode.y();
      this.state.text.scaleX = parseFloat(this.textNode.scaleX().toFixed(3));
      this.state.text.scaleY = parseFloat(this.textNode.scaleY().toFixed(3));
      this.state.text.rotation = parseFloat(this.textNode.rotation().toFixed(1));
    };
    this.textNode.on('dragend transformend', updateTextPos);

    this.enableSnapping(this.textNode);

    textContainer.add(this.textNode);
    this.mainLayer.batchDraw();
  }

  /**
   * Compute an appropriate cache pixelRatio that ensures the cached bitmap
   * always meets or exceeds the display resolution.
   *
   * Formula: ceil( max(|scaleX|,|scaleY|) × stageScale × devicePixelRatio )
   * with a floor of 2 to guarantee crisp rendering.
   *
   * @param {Konva.Node} node
   * @returns {number}
   */
  _cachePixelRatio(node) {
    const nodeScale = Math.max(Math.abs(node.scaleX()), Math.abs(node.scaleY()));
    const stageScale = this.stage?.scaleX() || 1;
    const dpr = window.devicePixelRatio || 2;
    return Math.max(2, Math.ceil(nodeScale * stageScale * dpr));
  }

  /**
   * Apply pixel-perfect hit detection to a Konva.Image node via
   * cache() + drawHitFromCache().  This is the only reliable Konva mechanism
   * for ignoring transparent pixels during pointer events.
   *
   * Quality is preserved by computing a pixelRatio high enough that the
   * cache bitmap always matches or exceeds the on-screen resolution.
   *
   * Transform events clear the cache (to avoid stale bitmap) and re-cache
   * on transformend at the new effective scale.
   *
   * @param {Konva.Image} node
   */
  _applyHitDetection(node) {
    try {
      const pr = this._cachePixelRatio(node);
      node.cache({ pixelRatio: pr });
      node.drawHitFromCache();
    } catch (e) {
      console.warn('[Scene] Hit-detection cache failed (likely CORS):', e);
    }

    node.on('transform', () => {
      node.clearCache();
    });

    node.on('transformend', () => {
      try {
        const pr = this._cachePixelRatio(node);
        node.cache({ pixelRatio: pr });
        node.drawHitFromCache();
      } catch (e) {
        console.warn('[Scene] Hit-detection re-cache failed:', e);
      }
    });
  }

  enableSnapping(node) {
    if (!node) return;

    node.dragBoundFunc((pos) => {
      const scale = this.stage.scaleX();
      let lx = pos.x / scale;
      let ly = pos.y / scale;

      const cw = this.canvasW;
      const ch = this.canvasH;
      const centerX = cw / 2;
      const centerY = ch / 2;
      const SNAP_THRESHOLD = 8; // pixels in logical space

      const rad = (node.rotation() * Math.PI) / 180;
      const w = node.width() * node.scaleX();
      const h = node.height() * node.scaleY();

      // Calculate logical center for this proposed position
      const proposedCenterX = lx + (w / 2) * Math.cos(rad) - (h / 2) * Math.sin(rad);
      const proposedCenterY = ly + (w / 2) * Math.sin(rad) + (h / 2) * Math.cos(rad);

      // Snap horizontally
      if (Math.abs(proposedCenterX - centerX) < SNAP_THRESHOLD) {
        lx += centerX - proposedCenterX;
      }

      // Snap vertically
      if (Math.abs(proposedCenterY - centerY) < SNAP_THRESHOLD) {
        ly += centerY - proposedCenterY;
      }

      return {
        x: lx * scale,
        y: ly * scale
      };
    });

    node.on('dragmove', () => {
      const centerX = this.canvasW / 2;
      const centerY = this.canvasH / 2;

      const rad = (node.rotation() * Math.PI) / 180;
      const w = node.width() * node.scaleX();
      const h = node.height() * node.scaleY();

      const nodeCenterX = node.x() + (w / 2) * Math.cos(rad) - (h / 2) * Math.sin(rad);
      const nodeCenterY = node.y() + (w / 2) * Math.sin(rad) + (h / 2) * Math.cos(rad);

      // Check if snapped (very close to center)
      const isSnappedX = Math.abs(nodeCenterX - centerX) < 0.1;
      const isSnappedY = Math.abs(nodeCenterY - centerY) < 0.1;

      this.vGuideLine.visible(isSnappedX);
      this.hGuideLine.visible(isSnappedY);

      this.transformerLayer.batchDraw();
    });

    node.on('dragend', () => {
      this.vGuideLine.visible(false);
      this.hGuideLine.visible(false);
      this.transformerLayer.batchDraw();
    });
  }

  // ==================== REFRESH SNAPSHOTS ====================

  refreshCharacterSnapshot(characterCreator, slot = 'character_1') {
    this.creators[slot] = characterCreator;
    this.characterCreator = characterCreator;
    this._elPremadeSrc[slot] = null;
    this.renderElement(slot);
    this.updateSlotThumbnail(slot);
  }

  refreshCarSnapshot(carCreator, slot = 'car_1') {
    this.creators[slot] = carCreator;
    this.carCreator = carCreator;
    this._elPremadeSrc[slot] = null;
    this.renderElement(slot);
    this.updateSlotThumbnail(slot);
  }

  setPremadeCharacter(src, slot) {
    this.characterCreator = null;
    if (slot) {
      this._elPremadeSrc[slot] = src;
      this.renderElement(slot);
      this.updateSlotThumbnail(slot);
    } else {
      // Set all character slots
      this.elements.filter(e => e.type === 'character').forEach(el => {
        this._elPremadeSrc[el.slot] = src;
        this.renderElement(el.slot);
        this.updateSlotThumbnail(el.slot);
      });
    }
  }

  setPremadeCar(src, slot) {
    this.carCreator = null;
    if (slot) {
      this._elPremadeSrc[slot] = src;
      this.renderElement(slot);
      this.updateSlotThumbnail(slot);
    } else {
      // Set all car slots
      this.elements.filter(e => e.type === 'car').forEach(el => {
        this._elPremadeSrc[el.slot] = src;
        this.renderElement(el.slot);
        this.updateSlotThumbnail(el.slot);
      });
    }
  }

  /** Set premade source for a specific element slot */
  setElementPremade(slot, src) {
    const el = this.elements.find(e => e.slot === slot);
    if (!el) return;
    this._elPremadeSrc[slot] = src;
    this.renderElement(slot);
    this.updateSlotThumbnail(slot);
  }

  getSlotThumbnailSrc(slot, type) {
    const premadeSrc = this._elPremadeSrc[slot];
    if (premadeSrc) {
      return resolveAssetUrl(premadeSrc);
    }
    const creator = this.creators[slot] || (type === 'character' ? this.characterCreator : (type === 'car' ? this.carCreator : null));
    if (creator) {
      try {
        return creator.toDataURL();
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  // ==================== CONTROLS UI ====================

  /** Helper: render a layer group card with optional reorder controls */
  _renderLayerGroupCard(html, group, activePose, showReorder, isFirst, isLast) {
    const selectedVariantId = this.state.layerSelections[group.id];
    const variant = group.variants.find(v => v.id === selectedVariantId);
    let thumbnailSrc = '';
    if (variant && variant.src) {
      const srcStr = typeof variant.src === 'object'
        ? (variant.src[activePose] || Object.values(variant.src)[0])
        : variant.src;
      thumbnailSrc = srcStr ? resolveAssetUrl(srcStr) : '';
    }
    const isVisible = this.state.elementVisibility[group.id] !== false;
    const isExpandable = group.variants && group.variants.length > 1;
    const reorderBtns = showReorder ? this._reorderButtonsHtml(group.id, isFirst, isLast) : '';
    const dragHandle = showReorder ? `<div class="layer-card__drag-handle" data-drag-handle="${group.id}" title="Drag to reorder">⠿</div>` : '';
    const slotAttr = showReorder ? ` data-layer-slot="${group.id}"` : '';

    html.push(`
      <div class="layer-card ${isExpandable ? 'layer-card--expandable' : ''} ${!isVisible ? 'layer-card--hidden' : ''}" data-layer-card="bg_${group.id}"${slotAttr}>
        <div class="layer-card__header" data-layer-toggle="bg_${group.id}">
          ${dragHandle}
          <div class="layer-card__thumbnail">
            ${thumbnailSrc ? `<img src="${thumbnailSrc}" alt="${group.name}" />` : `<span class="layer-card__fallback-icon">🖼️</span>`}
          </div>
          <span class="layer-card__title">${group.name}</span>
          <div class="layer-card__actions">
            ${reorderBtns}
            <button class="layer-card__btn layer-card__btn--flip" data-group-flip="${group.id}" title="Flip Horizontal">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 2 3 6 7 10"/><polyline points="17 2 21 6 17 10"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
            </button>
            <button class="layer-card__btn layer-card__btn--reset" data-group-reset="${group.id}" title="Reset Selection">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
            <button class="layer-card__btn layer-card__btn--visibility" data-group-toggle-btn="${group.id}" title="Toggle Visibility">
              ${isVisible
        ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
        : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`
      }
            </button>
            ${isExpandable 
              ? `<span class="layer-card__chevron"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg></span>` 
              : `<span class="layer-card__chevron layer-card__chevron--placeholder" style="width: 14px; height: 14px; visibility: hidden; margin-left: 4px; display: inline-block; flex-shrink: 0;"></span>`
            }
          </div>
        </div>
        ${isExpandable ? `
        <div class="layer-card__body">
          <div class="asset-grid">
            ${group.variants.map(v => {
              const srcStr = (v.src && typeof v.src === 'object') ? (v.src[activePose] || Object.values(v.src)[0]) : v.src;
              return `<div class="asset-item ${selectedVariantId === v.id ? 'asset-item--selected' : ''}" data-layer-group="${group.id}" data-variant-id="${v.id}"><div class="asset-item__preview">${srcStr ? `<img src="${resolveAssetUrl(srcStr)}" alt="${v.name}" loading="lazy" onerror="this.style.display='none'">` : '<span class="asset-item__none">✕</span>'}</div><span class="asset-item__name">${v.name}</span></div>`;
            }).join('')}
          </div>
        </div>` : ''}
      </div>
    `);
  }

  /** Helper: render the text overlay card with optional reorder controls */
  _renderTextCard(html, isFirst, isLast) {
    const isVisible = this.state.elementVisibility['text'] !== false;
    const colors = ['#ffffff', '#000000', '#f5222d', '#fa8c16', '#fadb14', '#52c41a', '#13c2c2', '#1890ff', '#2f54eb', '#722ed1', '#eb2f96'];
    const isCustomColor = !colors.includes(this.state.text.fill.toLowerCase());
    const showReorder = this._hasReorderable;
    const reorderBtns = showReorder ? this._reorderButtonsHtml('text', isFirst, isLast) : '';
    const dragHandle = showReorder ? `<div class="layer-card__drag-handle" data-drag-handle="text" title="Drag to reorder">⠿</div>` : '';
    const slotAttr = showReorder ? ' data-layer-slot="text"' : '';

    html.push(`
      <div class="layer-card layer-card--expandable ${!isVisible ? 'layer-card--hidden' : ''}" data-layer-card="text"${slotAttr}>
        <div class="layer-card__header" data-layer-toggle="text">
          ${dragHandle}
          <div class="layer-card__thumbnail">
            <span class="layer-card__fallback-icon" style="color:${this.state.text.fill}">T</span>
          </div>
          <span class="layer-card__title">Text</span>
          <div class="layer-card__actions">
            ${reorderBtns}
            <button class="layer-card__btn layer-card__btn--reset" data-text-reset="true" title="Reset Text">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            </button>
            <button class="layer-card__btn layer-card__btn--visibility" data-text-toggle-btn="true" title="Toggle Visibility">
              ${isVisible
        ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
        : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`}
            </button>
            <span class="layer-card__chevron"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg></span>
          </div>
        </div>
        <div class="layer-card__body">
          <div class="text-control">
            <input type="text" class="text-control__input" id="scene-text-input" value="${this.state.text.text}" placeholder="Enter text...">
            <div class="text-control__section">
              <span class="text-control__label" style="font-size:0.8rem;color:var(--dm-text-muted);">Font Size: <span id="scene-text-size-val">${this.state.text.fontSize}px</span></span>
              <input type="range" class="text-control__slider" id="scene-text-size" min="12" max="72" value="${this.state.text.fontSize}">
            </div>
            <div class="text-control__section">
              <div class="text-control__color-grid">
                ${colors.map(c => `<button class="text-control__color-swatch ${this.state.text.fill.toLowerCase() === c.toLowerCase() ? 'text-control__color-swatch--active' : ''}" style="background:${c}" data-text-color="${c}" title="${c}"></button>`).join('')}
                <div class="text-control__custom-color-wrap">
                  <button class="text-control__color-swatch text-control__color-swatch--custom ${isCustomColor ? 'text-control__color-swatch--active' : ''}" style="background:${isCustomColor ? this.state.text.fill : '#8c8c8c'}" title="Custom Color" id="custom-color-btn">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #fff; pointer-events: none;"><circle cx="12" cy="12" r="9"/><path d="M12 17a5 5 0 0 0 5-5c0-3-5-8-5-8s-5 5-5 8a5 5 0 0 0 5 5z"/></svg>
                  </button>
                  <input type="color" id="scene-text-color-picker" style="opacity:0;position:absolute;inset:0;width:100%;height:100%;cursor:pointer;" value="${this.state.text.fill}">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);
  }

  /** Helper: generate ↑/↓ reorder button HTML for a layer key */
  _reorderButtonsHtml(key, isFirst, isLast) {
    return `
      <button class="layer-card__btn layer-card__btn--reorder" data-reorder-up="${key}" title="Bring Forward" ${isFirst ? 'disabled' : ''}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>
      </button>
      <button class="layer-card__btn layer-card__btn--reorder" data-reorder-down="${key}" title="Send Backward" ${isLast ? 'disabled' : ''}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>
      </button>`;
  }

  /** Helper: render an element (character/car) card with reorder controls */
  _renderElementCard(html, el, showReorder, isFirst, isLast) {
    const isVisible = this.state.elementVisibility[el.slot] !== false;
    const thumbnailSrc = this.getSlotThumbnailSrc(el.slot, el.type);
    const icon = el.type === 'character' ? '🧑' : '🚗';
    const reorderBtns = showReorder ? this._reorderButtonsHtml(el.slot, isFirst, isLast) : '';
    const dragHandle = showReorder ? `<div class="layer-card__drag-handle" data-drag-handle="${el.slot}" title="Drag to reorder">⠿</div>` : '';
    const slotAttr = showReorder ? ` data-layer-slot="${el.slot}"` : '';

    html.push(`
      <div class="layer-card ${!isVisible ? 'layer-card--hidden' : ''}" data-layer-card="el_${el.slot}"${slotAttr}>
        <div class="layer-card__header" data-layer-toggle="el_${el.slot}">
          ${dragHandle}
          <div class="layer-card__thumbnail">
            ${thumbnailSrc ? `<img src="${thumbnailSrc}" alt="${el.label}" />` : `<span class="layer-card__fallback-icon">${icon}</span>`}
          </div>
          <span class="layer-card__title">${el.label}</span>
          <div class="layer-card__actions">
            ${reorderBtns}
            <button class="layer-card__btn layer-card__btn--flip" data-element-flip="${el.slot}" title="Flip Horizontal">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 2 3 6 7 10"/><polyline points="17 2 21 6 17 10"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
            </button>
            <button class="layer-card__btn layer-card__btn--reset" data-element-reset="${el.slot}" title="Reset Position">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
            <button class="layer-card__btn layer-card__btn--visibility" data-element-toggle-btn="${el.slot}" title="Toggle Visibility">
              ${isVisible
        ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
        : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`
      }
            </button>
            <span class="layer-card__chevron layer-card__chevron--placeholder" style="width: 14px; height: 14px; visibility: hidden; margin-left: 4px; display: inline-block; flex-shrink: 0;"></span>
          </div>
        </div>
      </div>
    `);
  }

  renderControls() {
    if (!this.controlsPanel) return;

    const html = [];

    const charCreatorForPose = this.creators['character_1'] || this.characterCreator;
    const activePoseForLayers = charCreatorForPose ? charCreatorForPose.state.pose : (this.design.defaultElements?.character_1?.pose || 'male_tdnb');

    // View Toggle selector if design has multiple views
    if (this.hasMultipleViews) {
      html.push(`
        <div class="view-toggle" style="margin-bottom: 20px;">
          <div class="mode-toggle">
            <button class="mode-toggle__btn ${this.state.activeView === 'front' ? 'mode-toggle__btn--active mode-tab--active' : ''}" data-view-select="front">Front View</button>
            <button class="mode-toggle__btn ${this.state.activeView === 'back' ? 'mode-toggle__btn--active mode-tab--active' : ''}" data-view-select="back">Back View (Customize)</button>
          </div>
        </div>
      `);
    }

    if (this.hasMultipleViews && this.state.activeView === 'front') {
      // Simple instruction block when front view is selected
      html.push(`
        <div class="view-instructions" style="text-align: center; padding: 40px 16px; color: var(--dm-text-muted);">
          <p style="font-size: 1.1rem; font-weight: 700; margin-bottom: 8px; color: #fff; font-family: 'Outfit', sans-serif;">Front of Shirt</p>
          <p style="font-size: 0.85rem; line-height: 1.5; font-family: 'Outfit', sans-serif;">This side features the exclusive AX title graphic.<br>Switch to the <strong>Back View</strong> to customize your characters and car!</p>
        </div>
      `);
    } else {
      // 1. Background Groups (below layers — fixed, no reorder)
      this.layerGroups.filter(g => g.zPosition !== 'above').forEach(group => {
        if (this.hasMultipleViews && group.id === 'back') return;
        this._renderLayerGroupCard(html, group, activePoseForLayers, false, false, false);
      });

      // 2. Unified reorderable list — all items in reverse layerOrder (top card = front-most)
      if (this._hasReorderable) {
        const reversedLayerOrder = [...this.state.layerOrder].reverse();
        reversedLayerOrder.forEach((key, displayIdx) => {
          const isFirst = displayIdx === 0;
          const isLast = displayIdx === reversedLayerOrder.length - 1;

          if (key === 'text' && this.state.showText) {
            this._renderTextCard(html, isFirst, isLast);
          } else {
            // Check if it's an element (character/car)
            const el = this.elements.find(e => e.slot === key);
            if (el) {
              this._renderElementCard(html, el, true, isFirst, isLast);
            } else {
              // Check if it's a custom layer instance
              const customInst = this._customInstances && this._customInstances.find(i => i.key === key);
              if (customInst && this._renderCustomInstanceCard) {
                this._renderCustomInstanceCard(html, customInst, activePoseForLayers, isFirst, isLast);
              } else {
                // It's a regular overlay layer group (skip 'front' from back customization UI)
                if (key === 'front') return;
                const group = this.layerGroups.find(g => g.id === key);
                if (group) this._renderLayerGroupCard(html, group, activePoseForLayers, true, isFirst, isLast);
              }
            }
          }
        });
      } else {
        // Single item or no items — no reorder UI needed
        this.elements.forEach(el => {
          this._renderElementCard(html, el, false, false, false);
        });
        this.layerGroups.filter(g => g.zPosition === 'above' && !g.isCustomTemplate).forEach(group => {
          if (group.id === 'front') return;
          this._renderLayerGroupCard(html, group, activePoseForLayers, false, false, false);
        });
        if (this.state.showText) this._renderTextCard(html, false, false);
      }

      // 3. Custom layer add controls
      if (this._renderCustomLayerControls) {
        this._renderCustomLayerControls(html);
      }
    }

    this.controlsPanel.innerHTML = html.join('');
    this.bindSceneControls();
    if (this._bindCustomLayerControls) this._bindCustomLayerControls();
    if (this._hasReorderable && (!this.hasMultipleViews || this.state.activeView === 'back')) {
      this.initLayerDragDrop();
    }
  }

  bindSceneControls() {
    // View selection
    this.controlsPanel.querySelectorAll('[data-view-select]').forEach(btn => {
      btn.addEventListener('click', async e => {
        const view = e.currentTarget.dataset.viewSelect;
        if (this.state.activeView !== view) {
          this.state.activeView = view;
          
          // Clear transformer selection
          if (this.transformer) {
            this.transformer.nodes([]);
            this.transformerLayer.batchDraw();
          }

          // Re-render controls & scene
          this.renderControls();
          await this.renderScene();
        }
      });
    });

    // Accordion toggles (expand/collapse only when clicking the chevron/collapse icon)
    this.controlsPanel.querySelectorAll('[data-layer-toggle]').forEach(header => {
      const chevron = header.querySelector('.layer-card__chevron');
      if (chevron) {
        chevron.addEventListener('click', e => {
          e.stopPropagation();
          const id = header.dataset.layerToggle;
          const card = this.controlsPanel.querySelector(`[data-layer-card="${id}"]`);
          if (card && card.classList.contains('layer-card--expandable')) {
            card.classList.toggle('layer-card--open');
          }
        });
      }
    });

    // Reorder buttons (↑ bring forward, ↓ send backward)
    this.controlsPanel.querySelectorAll('[data-reorder-up]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.reorderLayer(e.currentTarget.dataset.reorderUp, 'up');
      });
    });
    this.controlsPanel.querySelectorAll('[data-reorder-down]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.reorderLayer(e.currentTarget.dataset.reorderDown, 'down');
      });
    });

    // Layer selection
    this.controlsPanel.querySelectorAll('[data-layer-group]').forEach(item => {
      item.addEventListener('click', e => {
        const groupId = e.currentTarget.dataset.layerGroup;
        const variantId = e.currentTarget.dataset.variantId;
        this.state.layerSelections[groupId] = variantId;

        this.controlsPanel.querySelectorAll(`[data-layer-group="${groupId}"]`).forEach(el =>
          el.classList.remove('asset-item--selected')
        );
        e.currentTarget.classList.add('asset-item--selected');

        // Update card header thumbnail
        const card = this.controlsPanel.querySelector(`[data-layer-card="bg_${groupId}"]`);
        const variant = this.layerGroups.find(g => g.id === groupId).variants.find(v => v.id === variantId);
        if (card && variant && variant.src) {
          const img = card.querySelector('.layer-card__thumbnail img');
          if (img) img.src = resolveAssetUrl(variant.src);
        }

        this.renderBackground();
        this.renderOverlay();
      });
    });

    // Element visibility toggle
    this.controlsPanel.querySelectorAll('[data-element-toggle-btn]').forEach(btn => {
      btn.addEventListener('click', e => {
        const slot = e.currentTarget.dataset.elementToggleBtn;
        const isVisible = !this.state.elementVisibility[slot];
        this.state.elementVisibility[slot] = isVisible;

        const card = this.controlsPanel.querySelector(`[data-layer-card="el_${slot}"]`);
        if (card) {
          card.classList.toggle('layer-card--hidden', !isVisible);
          e.currentTarget.innerHTML = isVisible
            ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
            : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
        }

        const node = this._elNodes[slot];
        if (!isVisible && node && this.transformer.nodes().includes(node)) {
          this.transformer.nodes([]);
          this.transformerLayer.batchDraw();
        }

        this.renderElement(slot);
      });
    });

    // Element reset position
    this.controlsPanel.querySelectorAll('[data-element-reset]').forEach(btn => {
      btn.addEventListener('click', e => {
        const slot = e.currentTarget.dataset.elementReset;
        this._elSavedPos[slot] = null;
        const node = this._elNodes[slot];
        if (node) {
          const w = node.width();
          const h = node.height();
          const pos = this.resolveDefaultPos(slot, w, h);
          node.x(pos.x);
          node.y(pos.y);
          node.scaleX(pos.scaleX);
          node.scaleY(pos.scaleY);
          node.rotation(pos.rotation);
          this.transformer.nodes([]);
          this.mainLayer.batchDraw();
          this.transformerLayer.batchDraw();
          // Clear flip button active state
          const flipBtn = this.controlsPanel.querySelector(`[data-element-flip="${slot}"]`);
          if (flipBtn) flipBtn.classList.remove('is-flipped');
        }
      });
    });

    // Element flip horizontal
    this.controlsPanel.querySelectorAll('[data-element-flip]').forEach(btn => {
      btn.addEventListener('click', e => {
        const slot = e.currentTarget.dataset.elementFlip;
        const node = this._elNodes[slot];
        if (node) {
          // Shift x to keep visual center in place when mirroring
          const oldScaleX = node.scaleX();
          node.scaleX(-oldScaleX);
          node.x(node.x() + node.width() * oldScaleX);
          // Update saved position
          this._elSavedPos[slot] = {
            x: node.x(),
            y: node.y(),
            scaleX: parseFloat(node.scaleX().toFixed(3)),
            scaleY: parseFloat(node.scaleY().toFixed(3)),
            rotation: parseFloat(node.rotation().toFixed(1))
          };
          this.mainLayer.batchDraw();
          // Toggle visual active state on the button
          e.currentTarget.classList.toggle('is-flipped', node.scaleX() < 0);
        }
      });
    });

    // Overlay group flip horizontal
    this.controlsPanel.querySelectorAll('[data-group-flip]').forEach(btn => {
      btn.addEventListener('click', e => {
        const groupId = e.currentTarget.dataset.groupFlip;
        const node = this._overlayNodes[groupId];
        if (node) {
          // Shift x to keep visual center in place when mirroring
          const oldScaleX = node.scaleX();
          const savedPos = this._overlaySavedPos[groupId];
          const w = savedPos ? savedPos.width : (node.width ? node.width() : 0);
          node.scaleX(-oldScaleX);
          node.x(node.x() + w * oldScaleX);
          // Update saved position
          this._overlaySavedPos[groupId] = {
            x: node.x(),
            y: node.y(),
            scaleX: node.scaleX(),
            scaleY: node.scaleY(),
            rotation: node.rotation(),
            width: savedPos ? savedPos.width : 0,
            height: savedPos ? savedPos.height : 0
          };
          this.mainLayer.batchDraw();
          // Toggle visual active state on the button
          e.currentTarget.classList.toggle('is-flipped', node.scaleX() < 0);
        }
      });
    });

    // Background group visibility toggle
    this.controlsPanel.querySelectorAll('[data-group-toggle-btn]').forEach(btn => {
      btn.addEventListener('click', e => {
        const groupId = e.currentTarget.dataset.groupToggleBtn;
        const isVisible = this.state.elementVisibility[groupId] === false;
        this.state.elementVisibility[groupId] = isVisible;

        const card = this.controlsPanel.querySelector(`[data-layer-card="bg_${groupId}"]`);
        if (card) {
          card.classList.toggle('layer-card--hidden', !isVisible);
          e.currentTarget.innerHTML = isVisible
            ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
            : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
        }

        this.renderBackground();
        this.renderOverlay();
      });
    });

    // Background group reset
    this.controlsPanel.querySelectorAll('[data-group-reset]').forEach(btn => {
      btn.addEventListener('click', e => {
        const groupId = e.currentTarget.dataset.groupReset;
        const group = this.layerGroups.find(g => g.id === groupId);
        if (group && group.variants.length > 0) {
          const defaultVariantId = group.variants[0].id;
          this.state.layerSelections[groupId] = defaultVariantId;

          // Clear any saved drag/transform position so it resets to defaultPosition
          if (this._overlaySavedPos) {
            delete this._overlaySavedPos[groupId];
          }

          const card = this.controlsPanel.querySelector(`[data-layer-card="bg_${groupId}"]`);
          if (card) {
            card.querySelectorAll('[data-layer-group]').forEach(item => {
              item.classList.toggle('asset-item--selected', item.dataset.variantId === defaultVariantId);
            });
            const img = card.querySelector('.layer-card__thumbnail img');
            if (img && group.variants[0].src) {
              img.src = resolveAssetUrl(group.variants[0].src);
            }
          }

          this.renderBackground();
          this.renderOverlay();
        }
      });
    });

    // Text controls input
    const textInput = document.getElementById('scene-text-input');
    if (textInput) {
      textInput.addEventListener('input', e => {
        this.state.text.text = e.target.value;
        if (this.textNode) {
          this.textNode.text(e.target.value);
          this.mainLayer.batchDraw();
          if (this.transformerLayer) {
            this.transformer.forceUpdate();
            this.transformerLayer.batchDraw();
          }
        }
      });
    }

    // Text size range slider
    const textSize = document.getElementById('scene-text-size');
    const textSizeVal = document.getElementById('scene-text-size-val');
    if (textSize) {
      textSize.addEventListener('input', e => {
        const val = parseInt(e.target.value);
        this.state.text.fontSize = val;
        if (textSizeVal) textSizeVal.textContent = val + 'px';
        if (this.textNode) {
          this.textNode.fontSize(val);
          this.mainLayer.batchDraw();
          if (this.transformerLayer) {
            this.transformer.forceUpdate();
            this.transformerLayer.batchDraw();
          }
        }
      });
    }

    // Text color swatches
    this.controlsPanel.querySelectorAll('[data-text-color]').forEach(swatch => {
      swatch.addEventListener('click', e => {
        const color = e.currentTarget.dataset.textColor;
        this.state.text.fill = color;

        const card = this.controlsPanel.querySelector('[data-layer-card="text"]');
        if (card) {
          card.querySelectorAll('[data-text-color]').forEach(s => {
            s.classList.toggle('text-control__color-swatch--active', s.dataset.textColor === color);
          });
          const customColorBtn = document.getElementById('custom-color-btn');
          if (customColorBtn) {
            customColorBtn.classList.remove('text-control__color-swatch--active');
            customColorBtn.style.background = '#8c8c8c';
          }
          const thumbIcon = card.querySelector('.layer-card__thumbnail .layer-card__fallback-icon');
          if (thumbIcon) thumbIcon.style.color = color;
        }

        if (this.textNode) {
          this.textNode.fill(color);
          this.mainLayer.batchDraw();
          if (this.transformerLayer) {
            this.transformer.forceUpdate();
            this.transformerLayer.batchDraw();
          }
        }
      });
    });

    // Text color picker (custom color)
    const textColorPicker = document.getElementById('scene-text-color-picker');
    const customColorBtn = document.getElementById('custom-color-btn');
    if (textColorPicker) {
      textColorPicker.addEventListener('input', e => {
        const color = e.target.value;
        this.state.text.fill = color;

        const card = this.controlsPanel.querySelector('[data-layer-card="text"]');
        if (card) {
          // Deactivate all preset swatches
          card.querySelectorAll('[data-text-color]').forEach(s => {
            s.classList.remove('text-control__color-swatch--active');
          });
          // Activate custom color button and set background color
          if (customColorBtn) {
            customColorBtn.classList.add('text-control__color-swatch--active');
            customColorBtn.style.background = color;
          }
          const thumbIcon = card.querySelector('.layer-card__thumbnail .layer-card__fallback-icon');
          if (thumbIcon) thumbIcon.style.color = color;
        }

        if (this.textNode) {
          this.textNode.fill(color);
          this.mainLayer.batchDraw();
          if (this.transformerLayer) {
            this.transformer.forceUpdate();
            this.transformerLayer.batchDraw();
          }
        }
      });
    }

    // Text visibility toggle
    this.controlsPanel.querySelectorAll('[data-text-toggle-btn]').forEach(btn => {
      btn.addEventListener('click', e => {
        const isVisible = this.state.elementVisibility['text'] === false;
        this.state.elementVisibility['text'] = isVisible;

        const card = this.controlsPanel.querySelector('[data-layer-card="text"]');
        if (card) {
          card.classList.toggle('layer-card--hidden', !isVisible);
          e.currentTarget.innerHTML = isVisible
            ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
            : `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
        }

        if (!isVisible && this.textNode && this.transformer.nodes().includes(this.textNode)) {
          this.transformer.nodes([]);
          this.transformerLayer.batchDraw();
        }

        this.renderText();
      });
    });

    // Text Reset
    this.controlsPanel.querySelectorAll('[data-text-reset]').forEach(btn => {
      btn.addEventListener('click', e => {
        const defaults = this.sceneConfig.textDefaults;
        const designDefaults = this.design && this.design.textDefaults ? this.design.textDefaults : {};
        const textDefaults = { ...defaults, ...designDefaults };

        this.state.text = { ...this.state.text, ...textDefaults };

        if (this.textNode) {
          this.textNode.x(textDefaults.x);
          this.textNode.y(textDefaults.y);
          this.textNode.scaleX(1);
          this.textNode.scaleY(1);
          this.textNode.rotation(0);
          this.textNode.text(textDefaults.text);
          this.textNode.fontSize(textDefaults.fontSize);
          this.textNode.fill(textDefaults.fill);
          this.transformer.nodes([]);
          this.mainLayer.batchDraw();
          this.transformerLayer.batchDraw();
        }

        const card = this.controlsPanel.querySelector('[data-layer-card="text"]');
        if (card) {
          const textInput = card.querySelector('#scene-text-input');
          if (textInput) textInput.value = textDefaults.text;

          const textSize = card.querySelector('#scene-text-size');
          if (textSize) textSize.value = textDefaults.fontSize;

          const textSizeVal = card.querySelector('#scene-text-size-val');
          if (textSizeVal) textSizeVal.textContent = textDefaults.fontSize + 'px';

          const thumbIcon = card.querySelector('.layer-card__thumbnail .layer-card__fallback-icon');
          if (thumbIcon) thumbIcon.style.color = textDefaults.fill;

          card.querySelectorAll('[data-text-color]').forEach(swatch => {
            swatch.classList.toggle('text-control__color-swatch--active', swatch.dataset.textColor.toLowerCase() === textDefaults.fill.toLowerCase());
          });

          if (customColorBtn) {
            customColorBtn.classList.remove('text-control__color-swatch--active');
            customColorBtn.style.background = '#8c8c8c';
          }
          if (textColorPicker) {
            textColorPicker.value = textDefaults.fill;
          }
        }

        this.renderText();
      });
    });
  }

  // ==================== LAYER DRAG & DROP ====================

  /**
   * Initialize touch+mouse drag-and-drop for reorderable layer cards.
   * Drag handles trigger a ghost card that follows the pointer.
   * Drop position is determined by the vertical midpoint of sibling element cards.
   */
  initLayerDragDrop() {
    if (!this.controlsPanel) return;

    // Collect all element cards (these have data-layer-slot)
    const getElementCards = () => {
      return [...this.controlsPanel.querySelectorAll('[data-layer-slot]')];
    };

    let dragSlot = null;
    let ghost = null;
    let startY = 0;
    let cardRect = null;

    const onStart = (slot, clientY, card) => {
      if (!this.state.layerOrder.includes(slot)) return;

      dragSlot = slot;
      startY = clientY;
      cardRect = card.getBoundingClientRect();

      // Create ghost
      ghost = card.cloneNode(true);
      ghost.classList.add('layer-card--ghost');
      ghost.style.width = cardRect.width + 'px';
      ghost.style.left = cardRect.left + 'px';
      ghost.style.top = cardRect.top + 'px';
      document.body.appendChild(ghost);

      // Dim the original card
      card.style.opacity = '0.3';
    };

    const onMove = (clientY) => {
      if (!dragSlot || !ghost) return;

      // Move ghost
      const dy = clientY - startY;
      ghost.style.top = (cardRect.top + dy) + 'px';

      // Determine drop target by checking vertical midpoints of element cards
      const cards = getElementCards();
      cards.forEach(c => c.classList.remove('layer-card--drop-target'));

      for (const c of cards) {
        const cSlot = c.dataset.layerSlot;
        if (cSlot === dragSlot) continue;
        const rect = c.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (clientY < midY) {
          c.classList.add('layer-card--drop-target');
          break;
        }
      }
    };

    const onEnd = (clientY) => {
      if (!dragSlot) return;

      // Find drop index by checking card positions
      // Cards are displayed in reverse layerOrder (top = front-most)
      const cards = getElementCards();
      cards.forEach(c => {
        c.classList.remove('layer-card--drop-target');
        c.style.opacity = '';
      });

      // Find where we dropped in display order
      let dropDisplayIdx = cards.length - 1; // default: end (back-most)
      for (let i = 0; i < cards.length; i++) {
        const rect = cards[i].getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (clientY < midY) {
          dropDisplayIdx = i;
          break;
        }
      }

      // Convert display index (reversed) to layerOrder index
      // Display order is reversed: display[0] = layerOrder[last], display[last] = layerOrder[0]
      const order = this.state.layerOrder;
      const newOrderIdx = order.length - 1 - dropDisplayIdx;

      // Move layer to new position
      this.moveLayerToIndex(dragSlot, newOrderIdx);

      // Cleanup ghost
      if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
      ghost = null;
      dragSlot = null;
    };

    // Bind mouse events on drag handles
    this.controlsPanel.querySelectorAll('[data-drag-handle]').forEach(handle => {
      handle.addEventListener('mousedown', e => {
        e.preventDefault();
        e.stopPropagation();
        const slot = handle.dataset.dragHandle;
        const card = handle.closest('[data-layer-slot]');
        if (!card) return;
        onStart(slot, e.clientY, card);

        const onMouseMove = (ev) => onMove(ev.clientY);
        const onMouseUp = (ev) => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          onEnd(ev.clientY);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      // Touch events for iPad
      handle.addEventListener('touchstart', e => {
        e.preventDefault();
        e.stopPropagation();
        const slot = handle.dataset.dragHandle;
        const card = handle.closest('[data-layer-slot]');
        if (!card) return;
        const touch = e.touches[0];
        onStart(slot, touch.clientY, card);

        const onTouchMove = (ev) => {
          ev.preventDefault();
          onMove(ev.touches[0].clientY);
        };
        const onTouchEnd = (ev) => {
          handle.removeEventListener('touchmove', onTouchMove);
          handle.removeEventListener('touchend', onTouchEnd);
          handle.removeEventListener('touchcancel', onTouchEnd);
          const endY = ev.changedTouches?.[0]?.clientY ?? startY;
          onEnd(endY);
        };
        handle.addEventListener('touchmove', onTouchMove, { passive: false });
        handle.addEventListener('touchend', onTouchEnd);
        handle.addEventListener('touchcancel', onTouchEnd);
      }, { passive: false });
    });
  }

  // ==================== RECIPE ====================

  generateRecipe() {
    const recipe = {
      layerSelections: { ...this.state.layerSelections }
    };
    if (this.hasMultipleViews) {
      recipe.activeView = this.state.activeView;
    }
    // Only include text data if design supports text
    if (this.state.showText && this.state.text) {
      recipe.text = { ...this.state.text };
    }

    // Include overlay layer order if reorderable
    if (this._hasReorderable) {
      recipe.layerOrder = [...this.state.layerOrder];
    }

    // Include custom layer instances for admin reconstruction
    if (this._customInstances && this._customInstances.length > 0) {
      recipe.customInstances = this._customInstances.map(i => ({ key: i.key, group: i.groupId }));
    }

    // Per-element visibility and positions
    recipe.elementVisibility = { ...this.state.elementVisibility };
    recipe.elementPositions = {};
    for (const el of this.elements) {
      const node = this._elNodes[el.slot];
      if (node) {
        recipe.elementPositions[el.slot] = {
          type: el.type,
          x: Math.round(node.x()),
          y: Math.round(node.y()),
          scaleX: parseFloat(node.scaleX().toFixed(3)),
          scaleY: parseFloat(node.scaleY().toFixed(3)),
          rotation: parseFloat(node.rotation().toFixed(1)),
          width: node.width(),
          height: node.height()
        };
      }
    }

    if (this.textNode) {
      recipe.text.x = Math.round(this.textNode.x());
      recipe.text.y = Math.round(this.textNode.y());
      recipe.text.scaleX = parseFloat(this.textNode.scaleX().toFixed(3));
      recipe.text.scaleY = parseFloat(this.textNode.scaleY().toFixed(3));
      recipe.text.rotation = parseFloat(this.textNode.rotation().toFixed(1));
    }

    recipe.overlayPositions = {};
    for (const key in this._overlayNodes) {
      const node = this._overlayNodes[key];
      if (node) {
        // For wrapper groups (box composites), width/height are stored in _overlaySavedPos
        const savedPos = this._overlaySavedPos[key];
        recipe.overlayPositions[key] = {
          x: Math.round(node.x()),
          y: Math.round(node.y()),
          scaleX: parseFloat(node.scaleX().toFixed(3)),
          scaleY: parseFloat(node.scaleY().toFixed(3)),
          rotation: parseFloat(node.rotation().toFixed(1)),
          width: savedPos ? savedPos.width : (node.width ? node.width() : 0),
          height: savedPos ? savedPos.height : (node.height ? node.height() : 0)
        };
      }
    }

    // Include premade selections for box/box_car composite overlays
    if (this._customPremadeSelections && Object.keys(this._customPremadeSelections).length > 0) {
      recipe.customPremadeSelections = { ...this._customPremadeSelections };
    }

    return recipe;
  }
}
